import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CommonService } from '../../_services/common.service';
import { AuthService } from '../../_services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class HttpAPIInterceptor implements HttpInterceptor {
  private errorShown = false;
  constructor(
    private toasterService: ToastrService,
    private router: Router,
    private commonService: CommonService,
    private authService: AuthService) {
  }

  /**
   * Intercept request
   * @param req req to be modified
   * @param next next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestParams = req.params;
    // requestParams
    //Get auth token, pass it as bearer in reuest if its available
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    // Auth token available and header should be passed then pass the header
    // And If intercepter not needed then skip
    if (currentUser && (requestParams.get('interceptorNotNeeded') !== 'true' || req.url.includes('interceptorNotNeeded'))) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${currentUser.token}`
        }
      });
    }

    //Handle the request and response
    return next.handle(req).pipe(tap((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        // Show message in toaster (skip for AI endpoints — they handle feedback inline)
        const isAiRequest = req.url.includes('/ai/');
        if (typeof event.body.message === 'string' && event.body.message && !isAiRequest) {
          this.showError('success', event.body.message);
        }
        // Free users get a "X tokens used / Y remaining" info toast after
        // every successful AI action. The backend's `injectAiQuota`
        // middleware only attaches `aiQuota` for free users, so the
        // presence of the field is itself the gate.
        if (isAiRequest && event.body && event.body.success === true && event.body.aiQuota) {
          this.showAiQuotaToast(event.body.aiQuota);
        }
      }
    },
      (error: any) => {
        const isAiRequest = req.url.includes('/ai/');
        // AI requests surface the backend message globally so every AI
        // endpoint (summary, suggest, chat, enhance-text, similar-content,
        // similar-tangrams, extract-document,
        // finalize-plan, log_event, ...) gets consistent toastr feedback
        // without per-component handlers. Components keep their own error
        // callbacks for side-effects (clearing loaders, rolling back
        // optimistic UI) but should NOT show their own toastr.
        //
        // 403 + requiresSubscription = quota-exhausted → warning toastr.
        // Anything else (500, 502, network down, ...) → error toastr.
        if (isAiRequest) {
          const apiMessage = error.error && typeof error.error.message === 'string' && error.error.message;
          const fallback = (error.status === 0 || error.status === -1)
            ? 'Server not responding. Please try again.'
            : 'Something went wrong with the AI request. Please try again.';
          const isQuotaExhausted = error.status === 403 && error.error && error.error.requiresSubscription;
          this.showError(isQuotaExhausted ? 'warning' : 'error', apiMessage || fallback);
          return;
        }
        if(requestParams.get('interceptorNotNeeded') === 'true' || req.url.includes('interceptorNotNeeded')){
          return;
        }
        // Show error message in toaster
        if (error.error && typeof error.error.message === 'string') {
          this.showError('error', error.error.message);
          // this.toasterService.error(error.error.message);
        }
        if (error.status === 0 || error.status === -1) {
          this.showError('error', "Server Not Responding");
          // this.toasterService.error("Server Not Responding");
        }
        else if (error.status === 401) {
          // Only sign the user out when the 401 comes from OUR backend API. A
          // 401 from proxied/external media (Edlink files, the CORS worker, LMS
          // URLs) must surface as an error, never log the user out. The public
          // Edlink file/page proxy is on our own origin but is intentionally
          // token-free (it never returns 401), so exclude it as defence-in-depth.
          const url = req.url || '';
          const isOwnApi = url.startsWith(environment.baseUrl);
          const isEdlinkProxy = url.includes('/api/v1/edlink/file/') || url.includes('/api/v1/edlink/page/');
          if (isOwnApi && !isEdlinkProxy) {
            this.authService.logout();
            this.router.navigate(['login']);
          }
        }
        else if (error.status === 403) {
          this.router.navigate(['unauthorized']);
        }
        else if (error.status === 404) {
          this.showError('error', "Resource You Have Requested Not Found," + " URL: " + error.url);
          // this.toasterService.error("Resource You Have Requested Not Found," + " URL: " + error.url);
        }
        else if (error.status === 412) {
          this.authService.saveLoggedInUser(error.error);
          // Screen name is required for the registration, so when user do social login
          // and user has not added screen name then redirect to registration page for entering screen name
          this.commonService.raiseEventToHideRegistrationControlsExceptScreenName(error.error.data);
          this.router.navigate(['/register']);
        }
      }));
  }

  // Free-tier AI quota toast: shows the remaining credits as "X/Y" (e.g.
  // "4,000/5,000") so the user always sees what they have left. Backend
  // attaches the pre-formatted `remainingLabel` (see injectAiQuota); we keep
  // a client-side fallback in case an older response shape comes through.
  // Subscribed users / SUPER_USER never receive an `aiQuota` payload.
  private aiQuotaToastShown = false;
  showAiQuotaToast(quota: { used: number; limit: number; remaining: number; actionTokens: number; isFree?: boolean; remainingLabel?: string }): void {
    if (this.aiQuotaToastShown) { return; }
    this.aiQuotaToastShown = true;
    const actionTokens = Math.max(0, Number(quota.actionTokens) || 0);
    const remaining = Math.max(0, Number(quota.remaining) || 0);
    const limit = Math.max(0, Number(quota.limit) || 0);
    const remainingLabel = quota.remainingLabel || `${remaining.toLocaleString()}/${limit.toLocaleString()}`;
    const message = `Used ${actionTokens.toLocaleString()} token${actionTokens === 1 ? '' : 's'} — ${remainingLabel} credits remaining.`;
    if (remaining === 0) {
      this.toasterService.warning(message, 'AI credits exhausted');
    } else {
      this.toasterService.info(message, 'AI credits');
    }
    setTimeout(() => { this.aiQuotaToastShown = false; }, 1500);
  }

  showError(type: string, error: any): void {
    if(!this.errorShown){
      this.errorShown = true;
      if(type === 'success'){
        this.toasterService.success(error);
      }else if(type === 'error'){
        this.toasterService.error(error);
      }else if(type === 'warning'){
        this.toasterService.warning(error);
      }
    }
    setTimeout(() => {
      this.errorShown = false;
    }, 1000); // 5 seconds delay, adjust as needed
  }
}
